import { Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ContractorPortalUpdatePhoneSendCode } from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ContractorPortalUpdatePhoneModalValues } from './ContractorPortalUpdatePhoneModal.interface'
import { ContractorPortalUpdatePhoneModalSchema } from './ContractorPortalUpdatePhoneModal.validator'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ChevronLeftMinor } from '@shopify/polaris-icons'
import { parsePhoneNumber } from 'libphonenumber-js'

interface Props {
  token: string
  onClose: () => void
  onSuccess: (phone: string) => void
}

const initialValues: ContractorPortalUpdatePhoneModalValues = {
  phone: '',
}

const PHONE_MASK = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]

export const ContractorPortalUpdatePhoneModal = ({ token, onSuccess, onClose }: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const container = useMemo(() => document.getElementById('root'), [])

  const handleFormSubmit = async (values: ContractorPortalUpdatePhoneModalValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const parsedPhone = parsePhoneNumber(values.phone, 'US')

      const phone = parsedPhone?.formatInternational().replace(/\s+/g, '') || values.phone

      await createAsyncAction(
        dispatch,
        ContractorPortalUpdatePhoneSendCode.request({ phone, token }),
      )

      onSuccess(phone)
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
      disableScrollLock
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={ContractorPortalUpdatePhoneModalSchema}
      >
        {({ handleSubmit }) => (
          <div css={tw`max-w-[590px] mx-4 flex-col p-4 pt-6 bg-shades-0 rounded-2xl`}>
            <Button
              css={tw`bg-primary-100 h-[42px] w-[82px] hover:bg-primary-200`}
              color="light"
              size="medium"
              leftIcon={<ChevronLeftMinor width={19} css={tw`fill-primary-500`} />}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Typography css={tw`mt-6`} variant="h2">
              Update phone number
            </Typography>
            <Typography css={tw`text-shades-400 mt-2 mb-6 w-[326px]`}>
              Please enter your new phone number below
            </Typography>
            <FormikTextField
              name="phone"
              css={tw`w-full mb-6`}
              mask={PHONE_MASK}
              placeholder="Enter your phone number"
            />
            <FormErrorMessage css={tw`mb-6`} error={error} />
            <Button className={tw`w-full mb-6`} loading={isLoading} onClick={() => handleSubmit()}>
              Continue
            </Button>
          </div>
        )}
      </Formik>
    </Modal>
  )
}
